/**
 * Create Invoice Drawer - Form for creating new invoices
 */
import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter
} from '../../ui/sheet';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Loader2, FileText, AlertCircle } from 'lucide-react';
import { formatDateInput } from './utils';

export const CreateInvoiceDrawer = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  project,
  contact
}) => {
  const [formData, setFormData] = useState({
    description: '',
    due_date: '',
    line_items: [{ description: '', quantity: 1, unit_price: '', amount: '' }],
    memo: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when drawer opens
  useEffect(() => {
    if (isOpen) {
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      
      setFormData({
        description: project?.name ? `Invoice for ${project.name}` : '',
        due_date: formatDateInput(thirtyDaysFromNow),
        line_items: [{ description: project?.name || 'Services', quantity: 1, unit_price: '', amount: '' }],
        memo: ''
      });
      setError(null);
    }
  }, [isOpen, project]);

  // Calculate line item amount
  const updateLineItem = (index, field, value) => {
    const newLines = [...formData.line_items];
    newLines[index][field] = value;
    
    // Auto-calculate amount
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(newLines[index].quantity) || 0;
      const price = parseFloat(newLines[index].unit_price) || 0;
      newLines[index].amount = (qty * price).toFixed(2);
    }
    
    setFormData({ ...formData, line_items: newLines });
  };

  // Add line item
  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: '', amount: '' }]
    });
  };

  // Remove line item
  const removeLineItem = (index) => {
    if (formData.line_items.length <= 1) return;
    const newLines = formData.line_items.filter((_, i) => i !== index);
    setFormData({ ...formData, line_items: newLines });
  };

  // Calculate total
  const total = formData.line_items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  const handleSubmit = async () => {
    // Validation
    const validLines = formData.line_items.filter(l => l.amount && parseFloat(l.amount) > 0);
    if (validLines.length === 0) {
      setError('Please add at least one line item with an amount');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit({
        contact_id: contact?.id || project?.contact_id,
        contact_name: contact?.name || project?.contact_name || 'Customer',
        project_id: project?.id,
        project_name: project?.name,
        transaction_date: new Date().toISOString(),
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        line_items: validLines.map(l => ({
          description: l.description || 'Services',
          quantity: parseFloat(l.quantity) || 1,
          unit_price: parseFloat(l.unit_price) || parseFloat(l.amount),
          amount: parseFloat(l.amount)
        })),
        subtotal: total,
        tax_amount: 0,
        memo: formData.memo,
        requires_approval: true
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="create-invoice-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" />
            Create Invoice
          </SheetTitle>
          <SheetDescription>
            Create a new invoice for {project?.name || 'this project'}
          </SheetDescription>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Customer info (read-only) */}
          <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <Label className="text-xs text-slate-500">Bill To</Label>
            <p className="font-medium">{contact?.name || project?.contact_name || 'Customer'}</p>
          </div>

          {/* Due Date */}
          <div className="space-y-1.5">
            <Label htmlFor="due_date">Due Date</Label>
            <Input
              id="due_date"
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
            />
          </div>

          {/* Line Items */}
          <div className="space-y-3">
            <Label>Line Items</Label>
            {formData.line_items.map((item, idx) => (
              <div key={idx} className="p-3 border rounded-lg space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={item.description}
                    onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                    placeholder="Service description"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Unit Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.amount}
                      onChange={(e) => updateLineItem(idx, 'amount', e.target.value)}
                      placeholder="0.00"
                      className="font-medium"
                    />
                  </div>
                </div>
                {formData.line_items.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600 h-7"
                    onClick={() => removeLineItem(idx)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addLineItem}
            >
              + Add Line
            </Button>
          </div>

          {/* Total */}
          <div className="flex justify-between items-center p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
            <span className="font-medium">Total</span>
            <span className="text-xl font-bold">${total.toFixed(2)}</span>
          </div>

          {/* Memo */}
          <div className="space-y-1.5">
            <Label htmlFor="memo">Internal Memo (optional)</Label>
            <Textarea
              id="memo"
              value={formData.memo}
              onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
              placeholder="Internal notes..."
              className="min-h-[60px]"
            />
          </div>

          {/* Approval note */}
          <p className="text-xs text-slate-500">
            This invoice will require approval before it can be posted to the ledger.
          </p>
        </div>

        <SheetFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || total <= 0}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Invoice'
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default CreateInvoiceDrawer;
